import React, { useState } from 'react';
import {
  Box, VStack, Heading, Text, Button, ButtonText,
} from '@gluestack-ui/themed';
import { useContentStudioStore } from './store';
import { ConceptGraphVisualizer } from './ConceptGraphVisualizer';

export const Stage2GraphSelection = () => {
  const {
    stage1Output, setStage, setStage2Input, stage2Input,
  } = useContentStudioStore();
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>(
    stage2Input.selectedNodeIds || stage1Output?.recommendedNodeIds || [],
  );
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // For testing environments where onLayout might not trigger
  const effectiveDimensions = dimensions.width > 0 ? dimensions : { width: 800, height: 600 };

  if (!stage1Output) return null;

  const handleContinue = () => {
    setStage2Input({ ...stage2Input, selectedNodeIds });
    setStage(3);
  };

  const toggleNode = (id: string) => {
    if (selectedNodeIds.includes(id)) {
      setSelectedNodeIds((prev) => prev.filter((n) => n !== id));
    } else {
      setSelectedNodeIds((prev) => [...prev, id]);
    }
  };

  return (
    <Box flex={1} bg="$white">
      <VStack flex={1}>
        <Box px="$6" py="$4" bg="$bgApp" borderBottomWidth={1} borderColor="$borderLight">
          <Heading size="lg">Concept Selection</Heading>
          <Text color="$textSubtle" size="sm">
            Select the key concepts you want to cover. Drag to explore, scroll to zoom.
          </Text>
        </Box>

        {/* Full screen graph */}
        <Box
          flex={1}
          position="relative"
          onLayout={(e) => {
            const { width, height } = e.nativeEvent.layout;
            setDimensions({ width, height });
          }}
        >
          <ConceptGraphVisualizer
            nodes={stage1Output.graph.nodes}
            edges={stage1Output.graph.edges}
            clusters={stage1Output.graph.clusters}
            selectedNodeIds={selectedNodeIds}
            onToggleNode={toggleNode}
            width={effectiveDimensions.width}
            height={effectiveDimensions.height}
          />

          {/* Floating selection counter and button */}
          <Box
            position="absolute"
            bottom="$10"
            right="$10"
            bg="$white"
            p="$4"
            borderRadius="$lg"
            elevation={10}
            shadowColor="$black"
            shadowOffset={{ width: 0, height: 4 }}
            shadowOpacity={0.1}
            shadowRadius={10}
            borderWidth={1}
            borderColor="$borderLight"
          >
            <VStack space="md" alignItems="center">
              <Text size="sm" fontWeight="bold">
                {selectedNodeIds.length}
                {' '}
                Concepts Selected
              </Text>
              <Button
                onPress={handleContinue}
                isDisabled={selectedNodeIds.length === 0}
                size="md"
              >
                <ButtonText>Refine Scope</ButtonText>
              </Button>
            </VStack>
          </Box>

          <Box position="absolute" bottom="$6" left="$6" bg="$white" px="$2" py="$1" borderRadius="$sm" opacity={0.6}>
            <Text size="xs" color="$textHint">Tip: Drag nodes to organize, use wheel to zoom</Text>
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};
